import Helpers from './helpers';
import FormHelpers from './form-helpers';

const errorMessages = [];

const ErrorHandling = {
	validate(inputs) {
		if (errorMessages.length > 0) ErrorHandling.cleanUpErrorMessages();
		let valid = true;
		let firstErrorElement;

		const checkboxGroups = {};
		const otherCheckboxes = {};

		inputs.forEach((input) => {
			input.classList.remove('input--error', 'invalid');
			const validationType = input.dataset.validation;

			const isRequired = input.hasAttribute('required') || input.dataset.validation === 'required';
			if (isRequired) {
				const requiredValid = ErrorHandling.validateRequired(
					input,
					checkboxGroups,
					otherCheckboxes
				);
				valid = requiredValid && valid;

				if (!requiredValid && !firstErrorElement) {
					firstErrorElement = input;
				}
			}

			if (validationType) {
				switch (validationType) {
					case 'phoneNumber': {
						valid = FormHelpers.validatePhoneNumber(input.value) && valid;
						const phoneNumberInput = document.getElementById('phoneNumberInput');

						if (!firstErrorElement && phoneNumberInput.classList.contains('invalid'))
							firstErrorElement = phoneNumberInput;
						break;
					}

					case 'regex':
						valid = ErrorHandling.validateRegex(input) && valid;
						if (!firstErrorElement && input.classList.contains('invalid'))
							firstErrorElement = input;
						break;

					case 'date':
						valid = ErrorHandling.validateDate(input) && valid;
						if (!firstErrorElement && input.classList.contains('invalid'))
							firstErrorElement = input;
						break;

					default:
						break;
				}
			}
		});

		valid = ErrorHandling.validateCheckboxGroups(checkboxGroups, otherCheckboxes) && valid;

		// Validate textboxes associated with "Other" checkboxes
		Object.entries(otherCheckboxes).forEach(([groupId, isOtherSelected]) => {
			if (isOtherSelected) {
				valid = ErrorHandling.validateOtherTextbox(groupId) && valid;
			}
		});

		if (FormHelpers.validateRadioButtons(inputs)) {
			valid = false;
		}

		if (!valid) {
			firstErrorElement?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
		}
		return valid;
	},

	validateRequired(input, checkboxGroups, otherCheckboxes) {
		let valid = true;
		const updatedCheckboxGroups = { ...checkboxGroups };

		const groupContainer = input.closest('[data-required-group="true"]');
		if (groupContainer) {
			const groupId = groupContainer.dataset.groupId || groupContainer.id;
			valid =
				ErrorHandling.validateGroupContainer(input, groupContainer, groupId, otherCheckboxes) &&
				valid;
		} else if (input.type === 'checkbox' && input.dataset.groupRequired === 'true') {
			const groupName = input.name;
			if (!updatedCheckboxGroups[groupName]) updatedCheckboxGroups[groupName] = [];
			updatedCheckboxGroups[groupName].push(input);
		} else {
			valid = ErrorHandling.validateIndividualInput(input) && valid;
		}

		Object.assign(checkboxGroups, updatedCheckboxGroups);
		return valid;
	},

	validateGroupContainer(input, groupContainer, groupId, otherCheckboxes) {
		const isVisible = groupContainer.classList.contains('is-visible');
		if (!isVisible) {
			ErrorHandling.clearGroupErrors(groupContainer);
			return true;
		}

		const checkboxes = groupContainer.querySelectorAll('input[type="checkbox"]');
		const otherCheckbox = groupContainer.querySelector('input[id*="checkbox-other"]');
		const otherTextbox = groupContainer.querySelector('input[id*="text-input"]');

		// Track the state of the "Other" checkbox
		if (otherCheckbox) {
			// eslint-disable-next-line no-param-reassign
			otherCheckboxes[groupId] = !!otherCheckbox.checked;

			// If "Other" is unchecked, clear the associated textbox error
			if (!otherCheckbox.checked && otherTextbox) {
				otherTextbox.classList.remove('input--error', 'invalid');
			}
		}

		// Validate that at least one checkbox is selected
		if (input.type === 'checkbox' && !Array.from(checkboxes).some((checkbox) => checkbox.checked)) {
			checkboxes.forEach((checkbox) => checkbox.classList.add('input--error', 'invalid'));
			return false;
		}

		// Validate "Other" textbox when visible
		if (otherCheckbox && otherCheckbox.checked && otherTextbox) {
			return ErrorHandling.validateOtherTextbox(groupId);
		}

		return true;
	},

	validateOtherTextbox(groupId) {
		const groupContainer = document.querySelector(`[data-group-id="${groupId}"]`);
		const otherCheckbox = groupContainer?.querySelector('input[id*="checkbox-other"]');
		const otherTextbox = groupContainer?.querySelector('input[id*="text-input"]');

		// Only validate the textbox if the "Other" checkbox is checked
		if (otherCheckbox?.checked && otherTextbox) {
			if (!otherTextbox.value.trim()) {
				otherTextbox.classList.add('input--error', 'invalid');
				return false;
			}
			otherTextbox.classList.remove('input--error', 'invalid');
		}

		return true;
	},

	clearGroupErrors(groupContainer) {
		const inputsInGroup = groupContainer.querySelectorAll('input');
		inputsInGroup.forEach((elem) => elem.classList.remove('input--error', 'invalid'));
	},

	validateIndividualInput(input) {
		let valid = true;
		const minLength = input.dataset.minLength || 1;

		if (!input.value || !Helpers.checkWhitespace(input.value) || input.value.length < minLength) {
			input.classList.add('input--error', 'invalid');
			if (input.dataset.errorBlock) {
				ErrorHandling.createErrorBlock(input);
			}

			valid = false;
		}

		return valid;
	},

	validateRegex(input) {
		const pattern = input.getAttribute('pattern');
		if (pattern && !RegExp(pattern).test(input.value)) {
			input.classList.add('input--error', 'invalid');
			if (input.dataset.errorBlock) {
				ErrorHandling.createErrorBlock(input);
			}
			return false;
		}
		return true;
	},

	validateCheckboxGroups(checkboxGroups, otherCheckboxes) {
		let valid = true;
		Object.values(checkboxGroups).forEach((checkboxes) => {
			if (
				!checkboxes.some((checkbox) => checkbox.checked) &&
				!Object.values(otherCheckboxes).includes(true)
			) {
				checkboxes.forEach((checkbox) => checkbox.classList.add('input--error', 'invalid'));
				valid = false;
			}
		});
		return valid;
	},

	cleanUpErrorMessages() {
		errorMessages.forEach((errorBlockId) => {
			const errorBlock = document.getElementById(errorBlockId);
			if (errorBlock) errorBlock.classList.add('error-msg--hidden');
		});
		errorMessages.length = 0;
	},

	createErrorBlock(input) {
		const errorBlockId = input.dataset.errorBlock;
		if (!errorMessages.includes(errorBlockId)) {
			errorMessages.push(errorBlockId);
			const errorBlock = document.getElementById(errorBlockId);
			if (errorBlock) errorBlock.classList.remove('error-msg--hidden');
		}
	},

	validateDate(input) {
		const dateValue = input.value.trim();
		if (!dateValue) {
			input.classList.add('input--error', 'invalid');
			if (input.dataset.errorBlock) {
				ErrorHandling.createErrorBlock(input);
			}
			return false;
		}

		// Check if the date is valid
		const parsedDate = Date.parse(dateValue);
		if (parsedDate.isNaN) {
			input.classList.add('input--error', 'invalid');
			if (input.dataset.errorBlock) {
				ErrorHandling.createErrorBlock(input);
			}
			return false;
		}

		// Check if the date is within the range
		const minDate = input.getAttribute('min');
		const maxDate = input.getAttribute('max');

		if (
			(minDate && new Date(dateValue) < new Date(minDate)) ||
			(maxDate && new Date(dateValue) > new Date(maxDate))
		) {
			input.classList.add('input--error', 'invalid');
			if (input.dataset.errorBlock) {
				ErrorHandling.createErrorBlock(input);
			}
			return false;
		}

		input.classList.remove('input--error', 'invalid');
		return true;
	},
};

export default ErrorHandling.validate;
